import React, { useMemo } from 'react';
import { FilterField, SortSelect } from '../../Common/DataList';
import { changeTypeOptions, sortOptions, stateOptions } from './constants';
import { ChangeInstancesListConfig } from './types';

/**
 * The filter schema for the change instances list. Shared so every layout
 * variant offers exactly the same filters.
 */
const useChangeFilterFields = (
    config: ChangeInstancesListConfig,
    currentSort: { field?: string; order?: string } | undefined,
    setSort: (field: string, order: string) => void,
): FilterField[] => {
    const sortKey = `${currentSort?.field}:${currentSort?.order}`;

    return useMemo(() => [
        {
            // the number is what someone quotes when they are after one change;
            // the API matches it exactly, or a comma-separated few
            name: 'id',
            label: 'ID',
            type: 'search',
            placeholder: 'Search by ID',
            dataCy: 'id-search',
        },
        {
            // parent of the cascade, so it comes first
            name: 'consumer_team_id',
            label: 'Consumer Team',
            type: 'select',
            resource: config.teamsResource,
            dataCy: 'team-selector',
            hidden: !config.teamsResource,
        },
        {
            name: 'application_id',
            label: 'Application',
            type: 'select',
            resource: config.applicationsResource,
            dataCy: 'application-selector',
            // owners pick a team first; consumers have no team choice
            dependsOn: config.teamsResource ? ['consumer_team_id'] : undefined,
        },
        {
            name: 'service_item_id',
            label: 'Service Item',
            type: 'select',
            resource: config.serviceItemsResource,
            dataCy: 'service-item-selector',
            dependsOn: ['application_id'],
        },
        {
            name: 'service_id',
            label: 'Service',
            type: 'select',
            resource: config.servicesResource,
            dataCy: 'service-selector',
        },
        {
            name: 'state',
            label: 'State',
            type: 'select',
            multiple: true,
            options: stateOptions,
            dataCy: 'state-filter',
        },
        {
            name: 'change_type',
            label: 'Change Type',
            type: 'select',
            multiple: true,
            options: changeTypeOptions,
            dataCy: 'change-type-filter',
        },
        {
            // The submissions list deep-links here with a commit_id. Declaring
            // it keeps that link working and, unlike before, shows the user
            // which commit is filtering the list and lets them clear it.
            name: 'commit_id',
            label: 'Commit',
            type: 'search',
            placeholder: 'Search by commit',
            dataCy: 'commit-filter',
        },
        {
            // sorting lives on the list context, not in the filter values
            name: 'sort',
            label: 'Sort',
            type: 'custom',
            render: () => (
                <SortSelect options={sortOptions} currentSort={currentSort} setSort={setSort} />
            ),
        },
    ], [
        config.teamsResource, config.applicationsResource,
        config.serviceItemsResource, config.servicesResource,
        sortKey, setSort,
    ]);
};

export default useChangeFilterFields;
